import {
    ActionBar,
    BottomInfoBar,
    BottomInfoElement,
    Description,
    DialogWrapper,
    InfoText,
    Tags,
    TagWrapper
} from './FolderDetailsDialog.style';
import React, {useContext, useState} from 'react';
import {Button, Icon} from '@bosch/react-frok';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {t} from 'i18next';
import {formatDate, formatSize} from '../../../utils';
import {getLabel, isFolder} from '../../../utils/StorageObjectUtils';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {hasWriteOwnerAccess} from '../../../utils/UserUtils';
import {AuthContext} from '../../../context/AuthContext';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_FILE_COUNT,
    STORAGE_OBJECT_OBJECT_METADATA_FOLDER_COUNT,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';

export interface FolderDetailsDialogProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly editMode: boolean;
    editAction: () => void;
    deleteAction: () => void;
    copyUrlAction: () => void;
    onClose: () => void;
}

/**
 * @name FolderDetailsDialog
 * @description
 * The dialog containing information to the folder that has been clicked
 */
export const FolderDetailsDialog: React.VFC<FolderDetailsDialogProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isAuthorizedWriteOwnerAccess] = useState<boolean>(hasWriteOwnerAccess(isAuthenticated, roles, props.item.metadata.ownerRoles, props.item.metadata.parentOwnerRoles));
    const isEditModeAuthorized = (props.editMode && isAuthorizedWriteOwnerAccess) as boolean;
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FOLDER, props.item.templateId));
    const isFolderItem = isFolder(props.item.objectType);
    let label = getLabel(props.defaultLocale, props.item.labels);

    return (
        <DialogWrapper id={generateIdentifier()}
                       modal
                       open
                       title={t('FOLDER_DETAILS_DIALOG_TITLE')}
                       headlineSpace={props.editMode}
                       headline={(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                           && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)
                           && label.name) ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')}
                       onClose={props.onClose}>
            <ActionBar id={generateIdentifier()}
                       key={'command-bar'}
                       actions={[
                           <Button id={generateIdentifier()}
                                   key='copy-button'
                                   icon='copy'
                                   mode='tertiary'
                                   type='button'
                                   aria-label={t('GENERAL_COPY_URL_LABEL')}
                                   title={t('GENERAL_COPY_URL_LABEL')}
                                   onClick={props.copyUrlAction}/>,
                           isEditModeAuthorized &&
                           <Button id={generateIdentifier()}
                                   key='edit'
                                   icon='edit'
                                   mode='tertiary'
                                   type='button'
                                   title={t('GENERAL_EDIT_LABEL')}
                                   onClick={props.editAction}/>,
                           isEditModeAuthorized &&
                           <Button id={generateIdentifier()}
                                   key='delete'
                                   icon='delete'
                                   mode='tertiary'
                                   type='button'
                                   title={t('GENERAL_DELETE_LABEL')}
                                   onClick={props.deleteAction}/>
                       ]}
            />
            {isVisibleComponent(STORAGE_OBJECT_NAME, templatePropertySettings) &&
                <InfoText id={generateIdentifier()}>
                    {t('FOLDER_META_DATA_DIALOG_ORIGINAL_NAME_INFO_TEXT', {name: props.item.name ? props.item.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FOLDER_DETAILS_DIALOG_FOLDER_NAME_TEXT', {name: label.name ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </InfoText>
            }
            {(props.editMode && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FOLDER_DETAILS_PUBLIC_ACCESS', {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FOLDER_DETAILS_DIALOG_DATE_CREATED_INFO_TEXT', {date: props.item.metadata.dateCreated ? formatDate(props.language, props.item.metadata.dateCreated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FOLDER_DETAILS_DIALOG_DATE_UPDATED_INFO_TEXT', {date: props.item.metadata.dateUpdated ? formatDate(props.language, props.item.metadata.dateUpdated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('DATE_FROM_INFO_TEXT', {date: props.item.metadata.dateValidFrom ? formatDate(props.language, props.item.metadata.dateValidFrom) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('DATE_TO_INFO_TEXT', {date: props.item.metadata.dateValidTo ? formatDate(props.language, props.item.metadata.dateValidTo) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings)) &&
                <Description id={generateIdentifier()}>
                    {t('DESCRIPTION_INFO_TEXT', {description: label.description ? label.description : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </Description>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings)) &&
                <TagWrapper id={generateIdentifier()}>
                    {props.item.metadata.tags?.map(tag => {
                        return <Tags id={generateIdentifier()}
                                     key={tag}
                                     label={tag}/>;
                    })}
                </TagWrapper>
            }
            {(isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings)
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FOLDER_COUNT, templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FILE_COUNT, templatePropertySettings)))) &&
                <BottomInfoBar id={generateIdentifier()}>
                    {(isFolderItem
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FOLDER_COUNT, templatePropertySettings)) &&
                        <BottomInfoElement id={generateIdentifier()}>
                            <Icon id={generateIdentifier()}
                                  aria-label={t('GENERAL_FOLDER_LABEL')}
                                  iconName='folder'/>
                            {props.item.metadata.folderCount ?? 0}
                        </BottomInfoElement>
                    }
                    {(isFolderItem
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FILE_COUNT, templatePropertySettings)) &&
                        <BottomInfoElement>
                            <Icon id={generateIdentifier()}
                                  aria-label={t('GENERAL_LIST_LABEL')}
                                  iconName='list-view'/>
                            {props.item.metadata.fileCount ?? 0}
                        </BottomInfoElement>
                    }
                    {isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings) &&
                        <BottomInfoElement
                            id={generateIdentifier()}>{formatSize(props.item.totalSize)}</BottomInfoElement>
                    }
                </BottomInfoBar>
            }
        </DialogWrapper>
    );
};
